import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { GlassButton } from '@/components/GlassButton';
import { Reveal } from '@/components/Reveal';
import { SectionHeading } from '@/components/SectionHeading';
import { SpotlightCard } from '@/components/SpotlightCard';
import { useAppStore } from '@/lib/store';

type Cycle = 'monthly' | 'quarterly' | 'yearly';

const PLAN_PRICES: Record<Cycle, { price: string; cadence: string; perMonth: string; save?: string }> = {
  monthly:   { price: '¥29',  cadence: '/ 月', perMonth: '¥29.00 / 月' },
  quarterly: { price: '¥78',  cadence: '/ 季', perMonth: '¥26.00 / 月', save: '9 折' },
  yearly:    { price: '¥278', cadence: '/ 年', perMonth: '¥23.17 / 月', save: '8 折 · 省 ¥70' },
};

export function PlanPage(): JSX.Element {
  const user = useAppStore((s) => s.user);
  const setUser = useAppStore((s) => s.setUser);
  const aiUsed = useAppStore((s) => s.aiQuotaUsedToday);
  const [cycle, setCycle] = useState<Cycle>('yearly');
  const aiCap = user.plan === 'pro' ? 300 : 30;

  return (
    <div className="space-y-10">
      <Reveal>
        <div>
          <Link to="/app/profile" className="text-sm text-cream-400 hover:text-white font-arcade">
            ← 个人中心
          </Link>
          <div className="mt-3">
            <SectionHeading
              eyebrow="VIP · 会员计划"
              title={<>解锁全部课程 + <span className="text-gradient-warm">AI 高级版</span></>}
              align="left"
            />
          </div>
        </div>
      </Reveal>

      {/* Quota live indicator */}
      <Reveal delay={0.05}>
        <div className="rounded-3xl border border-honey-400/20 bg-honey-400/[0.04] p-5 flex flex-wrap items-center gap-4">
          <span className="text-2xl">🎯</span>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-semibold text-white">
              今日 AI 配额：<span className="font-mono text-honey-300">{aiUsed} / {aiCap}</span> 次
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-honey-400 to-mint-400"
                animate={{ width: `${Math.min(100, (aiUsed / aiCap) * 100)}%` }}
              />
            </div>
          </div>
          {user.plan === 'free' && aiUsed >= 20 && (
            <span className="text-xs text-ember-300 font-arcade">⚠ 快用完了</span>
          )}
        </div>
      </Reveal>

      {/* Cycle toggle */}
      <Reveal delay={0.08}>
        <div className="inline-flex gap-1 rounded-full bg-ink-800/60 border border-honey-400/20 p-1">
          {(['monthly', 'quarterly', 'yearly'] as Cycle[]).map((k) => (
            <button
              key={k}
              onClick={() => setCycle(k)}
              className={`px-4 py-2 rounded-full text-xs font-arcade tracking-widest transition-all ${
                cycle === k
                  ? 'bg-honey-400/20 text-honey-200 glow-cyan'
                  : 'text-cream-400 hover:text-white'
              }`}
            >
              {k === 'monthly' ? '月付' : k === 'quarterly' ? '季付 9折' : '年付 8折'}
            </button>
          ))}
        </div>
      </Reveal>

      <div className="grid gap-6 md:grid-cols-2">
        <Reveal delay={0.05}>
          <SpotlightCard className="h-full">
            <div className="text-sm font-semibold text-cream-300">Free</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-5xl font-extrabold text-white">¥0</span>
              <span className="text-sm text-cream-400">永久</span>
            </div>
            <ul className="mt-6 space-y-2.5 text-sm text-cream-200">
              <Row ok>核心玩法（拼图 / 打字 / 口语）永久免费</Row>
              <Row ok>无限畅玩 · 没有"体力值"</Row>
              <Row ok>最多 <strong>10 门</strong>课程</Row>
              <Row ok>最多 <strong>2 个</strong>单词学习计划</Row>
              <Row ok>AI 智能纠错 · 基础版</Row>
              <Row ok>AI 口语测评 <strong>30 次 / 日</strong></Row>
              <Row>课程市场全库（限 10 门）</Row>
              <Row>AI Pro 引擎（更精准的语法诊断）</Row>
              <Row>推广分佣权益</Row>
            </ul>
            {user.plan === 'free' ? (
              <div className="mt-8 rounded-full bg-ink-800/60 border border-white/[0.08] text-center py-2.5 text-sm text-cream-300 font-arcade">
                CURRENT
              </div>
            ) : (
              <button
                onClick={() => setUser({ plan: 'free' })}
                className="mt-8 w-full rounded-full bg-ink-800/60 border border-white/[0.08] py-2.5 text-sm text-cream-300 hover:bg-ink-700/60"
              >
                降级到 Free
              </button>
            )}
          </SpotlightCard>
        </Reveal>

        <Reveal delay={0.1}>
          <SpotlightCard
            spotlightColor="rgba(0, 240, 255, 0.32)"
            className="h-full border-honey-400/40 bg-honey-400/[0.04]"
          >
            <div className="absolute -top-3 right-6 rounded-full bg-gradient-to-r from-honey-400 to-ember-500 px-3 py-1 text-xs font-arcade text-ink-950">
              最热门
            </div>
            <div className="text-sm font-semibold text-honey-300">VIP</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-5xl font-extrabold text-white">{PLAN_PRICES[cycle].price}</span>
              <span className="text-sm text-cream-400">{PLAN_PRICES[cycle].cadence}</span>
              {PLAN_PRICES[cycle].save !== undefined && (
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-ember-500/20 text-ember-300 border border-ember-400/40">
                  {PLAN_PRICES[cycle].save}
                </span>
              )}
            </div>
            <div className="text-xs text-cream-400 mt-1 font-mono">{PLAN_PRICES[cycle].perMonth}</div>

            <ul className="mt-6 space-y-2.5 text-sm text-cream-200">
              <Row ok>核心玩法（拼图 / 打字 / 口语）永久免费</Row>
              <Row ok>无限畅玩 · 没有"体力值"</Row>
              <Row ok><strong>全库 1000+</strong> 套课程无限解锁</Row>
              <Row ok><strong>无限</strong>单词学习计划</Row>
              <Row ok>AI 智能纠错 · <span className="text-honey-300">高级版</span></Row>
              <Row ok>AI 口语测评 <strong>300 次 / 日</strong></Row>
              <Row ok>课程市场全库 · 不限门数</Row>
              <Row ok>K12 教材（人教 / 外研 / 北师大）同步</Row>
              <Row ok>推广分佣 · 拉新得高额佣金</Row>
            </ul>

            {user.plan === 'pro' ? (
              <div className="mt-8 rounded-full bg-honey-400/15 border border-honey-400/40 text-center py-2.5 text-sm text-honey-200 font-arcade glow-cyan">
                ★ ACTIVE
              </div>
            ) : (
              <GlassButton
                onClick={() => setUser({ plan: 'pro' })}
                size="md"
                variant="solid"
                className="mt-8 w-full"
              >
                立即开通 VIP · {PLAN_PRICES[cycle].price}
              </GlassButton>
            )}
          </SpotlightCard>
        </Reveal>
      </div>

      <Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          <Info emoji="💳" title="支付方式" body="微信支付 / 支付宝" />
          <Info emoji="🛡️" title="7 天无理由退款" body="开通后 7 天内不满意，全额退款" />
          <Info emoji="🔄" title="多端同步" body="网页 / iOS / Android / 小程序 进度实时同步" />
        </div>
      </Reveal>

      <Reveal>
        <div className="rounded-3xl border border-honey-400/20 bg-honey-400/[0.04] p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-display font-bold text-white">想白嫖 VIP？</div>
            <div className="text-sm text-cream-300 mt-1">推荐朋友注册 · 每个 Pro 转化分你 ¥30</div>
          </div>
          <GlassButton to="/app/invite" size="md">
            🎁 看分佣计划
          </GlassButton>
        </div>
      </Reveal>
    </div>
  );
}

function Row({ ok = false, children }: { ok?: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <li className="flex gap-2 items-start">
      <span className={ok ? 'text-mint-400 shrink-0' : 'text-cream-500 shrink-0'}>
        {ok ? '✓' : '—'}
      </span>
      <span className={ok ? '' : 'opacity-60 line-through decoration-cream-500/40'}>{children}</span>
    </li>
  );
}

function Info({ emoji, title, body }: { emoji: string; title: string; body: string }): JSX.Element {
  return (
    <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="text-2xl">{emoji}</div>
      <div className="mt-2 font-display font-bold text-white">{title}</div>
      <div className="mt-1 text-xs text-cream-400">{body}</div>
    </div>
  );
}
